import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "./api";
import { PageHeader, TextInput, Button, Card } from "./ui";
import { Save, Plus, Trash2 } from "lucide-react";

const MATERIAL_KEYS = ["PLA", "PETG", "ABS", "TPU", "RESIN"];

const PRICING_DEFAULTS = () => ({
  setup_fee: 180,
  minimum_price: 300,
  machine_hourly_rate: 220,
  profit_multiplier: 1.35,
  grams_per_print_hour: 35,
  default_infill_factor: 0.18,
  default_shell_factor: 0.3,
  default_wall_count: 2,
  default_layer_height_mm: 0.2,
  risk_multiplier_low: 0.06,
  risk_multiplier_medium: 0.14,
  risk_multiplier_high: 0.24,
  pricing_engine_version: "quote-analysis-v5",
  material_price_per_gram: {
    PLA: 2.2, PETG: 2.8, ABS: 3.0, TPU: 4.5, RESIN: 6.5,
  },
  material_density_g_cm3: {
    PLA: 1.24, PETG: 1.27, ABS: 1.04, TPU: 1.21, RESIN: 1.10,
  },
  bulk_tiers: [{ qty: 3, factor: 0.92 }, { qty: 5, factor: 0.86 }, { qty: 10, factor: 0.78 }],
});

export default function AdminPricing() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  const dflt = PRICING_DEFAULTS();
  const merged = { ...dflt, ...(data?.pricing || {}) };
  merged.material_price_per_gram = { ...dflt.material_price_per_gram, ...(merged.material_price_per_gram || {}) };
  merged.material_density_g_cm3 = { ...dflt.material_density_g_cm3, ...(merged.material_density_g_cm3 || {}) };
  if (merged.default_infill_factor == null && merged.infill_factor != null) merged.default_infill_factor = merged.infill_factor;
  if (merged.default_shell_factor == null && merged.shell_factor != null) merged.default_shell_factor = merged.shell_factor;
  merged.default_infill_factor = merged.default_infill_factor ?? merged.infill_factor ?? 0.18;
  merged.default_shell_factor = merged.default_shell_factor ?? merged.shell_factor ?? 0.3;
  merged.grams_per_print_hour = merged.grams_per_print_hour ?? 35;
  merged.default_wall_count = merged.default_wall_count ?? 2;
  merged.default_layer_height_mm = merged.default_layer_height_mm ?? 0.2;

  const p = merged;

  const updP = (k, v) => setData((s) => {
    const nextPricing = { ...s.pricing, [k]: v };
    if (k === "default_infill_factor") nextPricing.infill_factor = v;
    if (k === "default_shell_factor") nextPricing.shell_factor = v;
    return { ...s, pricing: nextPricing };
  });

  const updMaterial = (key, value) => {
    updP("material_price_per_gram", {
      ...(p.material_price_per_gram || {}),
      [key]: parseFloat(value) || 0,
    });
  };

  const updDensity = (key, value) => {
    updP("material_density_g_cm3", {
      ...(p.material_density_g_cm3 || {}),
      [key]: parseFloat(value) || 0,
    });
  };

  const updBulk = (i, k, v) => {
    const t = [...(p.bulk_tiers || [])];
    t[i] = { ...t[i], [k]: k === "factor" ? parseFloat(v) : parseInt(v, 10) };
    updP("bulk_tiers", t);
  };
  const addBulk = () => updP("bulk_tiers", [...(p.bulk_tiers || []), { qty: 1, factor: 1 }]);
  const removeBulk = (i) => updP("bulk_tiers", p.bulk_tiers.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { data });
      toast.success("Fiyatlandırma kaydedildi");
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  if (!data) return <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Yükleniyor…</div>;

  return (
    <div>
      <PageHeader
        title="Fiyatlandırma Motoru"
        description="3D baskı fiyat motoru katsayıları, süre çıktısı (g/saat) ve müşteri arayüzündeki varsayılan baskı profili referansları."
        right={<Button onClick={save} disabled={busy} data-testid="pricing-save"><Save className="w-4 h-4" /> Kaydet</Button>}
      />

      <div className="space-y-6">
        <Card className="p-6 md:p-8">
          <h2 className="font-display text-lg font-bold uppercase tracking-tight mb-6 text-[#FF4F00]">Genel ücret ve motor</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TextInput label="Kurulum (setup_fee) ₺" type="number" value={p.setup_fee} onChange={(e) => updP("setup_fee", parseFloat(e.target.value) || 0)} testid="p-setup" />
            <TextInput label="Minimum fiyat (minimum_price) ₺" type="number" value={p.minimum_price} onChange={(e) => updP("minimum_price", parseFloat(e.target.value) || 0)} testid="p-min" />
            <TextInput label="Makine saati (machine_hourly_rate) ₺/saat" type="number" value={p.machine_hourly_rate} onChange={(e) => updP("machine_hourly_rate", parseFloat(e.target.value) || 0)} testid="p-hourly" />
            <TextInput label="Baskı hızını referans (grams_per_print_hour) g/saat" type="number" step="0.1" value={p.grams_per_print_hour ?? 35} onChange={(e) => updP("grams_per_print_hour", parseFloat(e.target.value) || 0)} testid="p-gph" />
            <TextInput label="Kar çarpanı (profit_multiplier)" type="number" step="0.01" value={p.profit_multiplier} onChange={(e) => updP("profit_multiplier", parseFloat(e.target.value) || 0)} testid="p-profit" />
            <TextInput label="Fiyat motoru sürümü (pricing_engine_version)" value={p.pricing_engine_version ?? "quote-analysis-v5"} onChange={(e) => updP("pricing_engine_version", e.target.value)} testid="p-engine-version" />
          </div>
        </Card>

        <Card className="p-6 md:p-8">
          <h2 className="font-display text-lg font-bold uppercase tracking-tight mb-6 text-[#FF4F00]">Varsayılan baskı modeli referansları</h2>
          <p className="text-xs text-zinc-500 mb-6 font-mono">Müşteri ayar seçmezse normalize edilir; motor shell ve katman süresi bunlarla uyumlu çalışır.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TextInput label="Varsayılan dolgu oranı (default_infill_factor) 0–1" type="number" step="0.01" value={p.default_infill_factor ?? 0.18} onChange={(e) => updP("default_infill_factor", parseFloat(e.target.value) || 0)} testid="p-default-infill-factor" />
            <TextInput label="Varsayıvan shell katsayısı (default_shell_factor)" type="number" step="0.01" value={p.default_shell_factor ?? 0.3} onChange={(e) => updP("default_shell_factor", parseFloat(e.target.value) || 0)} testid="p-default-shell-factor" />
            <TextInput label="Referans duvar sayısı (default_wall_count)" type="number" value={p.default_wall_count ?? 2} onChange={(e) => updP("default_wall_count", parseInt(e.target.value, 10) || 1)} testid="p-default-wall" />
            <TextInput label="Referans katman mm (default_layer_height_mm)" type="number" step="0.01" value={p.default_layer_height_mm ?? 0.2} onChange={(e) => updP("default_layer_height_mm", parseFloat(e.target.value) || 0)} testid="p-default-layer" />
            <TextInput label="Risk — düşük (risk_multiplier_low)" type="number" step="0.01" value={p.risk_multiplier_low} onChange={(e) => updP("risk_multiplier_low", parseFloat(e.target.value) || 0)} testid="p-risk-low" />
            <TextInput label="Risk — orta (risk_multiplier_medium)" type="number" step="0.01" value={p.risk_multiplier_medium} onChange={(e) => updP("risk_multiplier_medium", parseFloat(e.target.value) || 0)} testid="p-risk-medium" />
            <TextInput label="Risk — yüksek (risk_multiplier_high)" type="number" step="0.01" value={p.risk_multiplier_high} onChange={(e) => updP("risk_multiplier_high", parseFloat(e.target.value) || 0)} testid="p-risk-high" />
          </div>
        </Card>

        <Card className="p-6 md:p-8">
          <h2 className="font-display text-lg font-bold uppercase tracking-tight mb-6 text-[#FF4F00]">Malzeme fiyatı (material_price_per_gram) ₺/g</h2>
          <div className="space-y-2" data-testid="p-rates">
            <div className="grid grid-cols-12 gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-2">
              <div className="col-span-4">Malzeme</div>
              <div className="col-span-8">₺ / gram</div>
            </div>
            {MATERIAL_KEYS.map((k) => (
              <div key={k} className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-4 font-display font-bold uppercase">{k}</div>
                <TextInput className="col-span-8" type="number" step="0.01" value={p.material_price_per_gram?.[k] ?? ""} onChange={(e) => updMaterial(k, e.target.value)} />
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 md:p-8">
          <h2 className="font-display text-lg font-bold uppercase tracking-tight mb-6 text-[#FF4F00]">Malzeme yoğunluğu (material_density_g_cm3) g/cm³</h2>
          <div className="space-y-2">
            {MATERIAL_KEYS.map((k) => (
              <div key={k} className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-4 font-display font-bold uppercase">{k}</div>
                <TextInput className="col-span-8" type="number" step="0.001" value={p.material_density_g_cm3?.[k] ?? ""} onChange={(e) => updDensity(k, e.target.value)} />
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-lg font-bold uppercase tracking-tight text-[#FF4F00]">Adet indirim kademeleri (bulk_tiers)</h2>
            <Button variant="secondary" onClick={addBulk} data-testid="p-add-bulk"><Plus className="w-4 h-4" /> Kademe</Button>
          </div>
          <div className="space-y-2" data-testid="p-bulks">
            <div className="grid grid-cols-12 gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-2">
              <div className="col-span-5">Min adet (qty)</div>
              <div className="col-span-6">Çarpan (factor)</div>
              <div />
            </div>
            {(p.bulk_tiers || []).map((t, i) => (
              <div key={i} className="grid grid-cols-12 gap-3 items-center">
                <TextInput className="col-span-5" type="number" value={t.qty} onChange={(e) => updBulk(i, "qty", e.target.value)} />
                <TextInput className="col-span-6" type="number" step="0.01" value={t.factor} onChange={(e) => updBulk(i, "factor", e.target.value)} />
                <button type="button" onClick={() => removeBulk(i)} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
